import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import checkoutService, {
  CheckoutResponse,
  SubscriptionPlan,
} from '../services/checkoutService';

type BillingType = 'CREDIT_CARD';

const validateCPF = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, '');

  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false; // Todos dígitos iguais

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned[10])) return false;

  return true;
};

const validateCNPJ = (cnpj: string): boolean => {
  const cleaned = cnpj.replace(/\D/g, '');

  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weights1[i];
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== parseInt(cleaned[12])) return false;

  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weights2[i];
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digit2 !== parseInt(cleaned[13])) return false;

  return true;
};

const validateCPFOrCNPJ = (value: string): boolean => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 11) return validateCPF(value);
  if (cleaned.length === 14) return validateCNPJ(value);
  return false;
};

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [name, setName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [billingType] = useState<BillingType>('CREDIT_CARD');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);

  // Carrega o plano (preço + trial) do backend
  useEffect(() => {
    checkoutService
      .getPlan()
      .then(setPlan)
      .catch(() =>
        // Fallback pros valores default se o endpoint estiver offline
        setPlan({
          monthlyPrice: 14.9,
          currency: 'BRL',
          trialDays: 7,
          formattedPrice: 'R$ 14,90',
        }),
      );
  }, []);

  // Buscar dados do usuário quando email estiver preenchido
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (email && email.includes('@')) {
        const userInfo = await checkoutService.getUserInfo(email);
        if (userInfo.found && userInfo.name) {
          setName(userInfo.name);
        }
      }
    };
    fetchUserInfo();
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validação dos campos obrigatórios
    if (!email.trim()) {
      setError('Email é obrigatório');
      return;
    }
    if (!name.trim()) {
      setError('Nome completo é obrigatório');
      return;
    }
    if (!cpfCnpj.trim()) {
      setError('CPF/CNPJ é obrigatório');
      return;
    }
    if (!phone.trim()) {
      setError('Telefone é obrigatório');
      return;
    }
    if (!validateCPFOrCNPJ(cpfCnpj)) {
      setError('CPF ou CNPJ inválido');
      return;
    }

    setLoading(true);

    try {
      const response = await checkoutService.createCheckout({
        email,
        billingType,
        name,
        cpfCnpj,
        phone,
      });

      // Se tiver paymentUrl, redireciona para o Asaas completar o pagamento
      if (response.paymentUrl) {
        window.location.href = response.paymentUrl;
        return;
      }

      // Caso contrário, mostra tela de sucesso (não deveria acontecer para cartão)
      setCheckoutData(response);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Erro ao processar checkout. Tente novamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Acesso ilimitado a todos os devocionais',
    'Novos conteúdos toda semana',
    'Histórico de progresso sincronizado',
    'Notificações personalizadas',
    'Cancele quando quiser',
  ];

  if (!plan) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (success && checkoutData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Box textAlign="center" mb={3}>
              <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Assinatura Criada!
              </Typography>
              <Typography color="text.secondary">
                Seu cartão será cobrado automaticamente após o período de trial.
              </Typography>
            </Box>

            {checkoutData.paymentUrl && (
              <Box textAlign="center" mb={3}>
                <Button
                  variant="contained"
                  size="large"
                  href={checkoutData.paymentUrl}
                  target="_blank"
                >
                  Completar Pagamento
                </Button>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Trial de {plan.trialDays} dias:</strong> Você terá
                acesso imediato a todo o conteúdo. A primeira cobrança de{' '}
                {plan.formattedPrice} será realizada após{' '}
                {plan.trialDays} dias.
              </Typography>
            </Alert>

            <Box textAlign="center" mt={3}>
              <Button
                variant="text"
                onClick={() => navigate('/explore')}
              >
                Voltar para o App
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Semente Diária
            </Typography>
            <Typography variant="h6" color="text.secondary" mt={1}>
              Premium
            </Typography>
          </Box>

          {/* Preço */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {plan.trialDays} dias grátis, depois
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary.main">
              {plan.formattedPrice}
              <Typography
                component="span"
                variant="body1"
                color="text.secondary"
              >
                /mês
              </Typography>
            </Typography>
          </Box>

          {/* Benefícios */}
          <List dense sx={{ mb: 3 }}>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={benefit} />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              helperText="Use o mesmo email do cadastro no app"
            />

            <TextField
              fullWidth
              label="Nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="CPF/CNPJ"
              value={cpfCnpj}
              onChange={(e) => setCpfCnpj(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Telefone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              sx={{ mb: 3 }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Forma de Pagamento */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 3,
                p: 2,
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                bgcolor: 'rgba(76, 175, 80, 0.05)',
              }}
            >
              <CreditCardIcon color="primary" />
              <Typography variant="body1" fontWeight="medium">
                Pagamento com Cartão de Crédito
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading || !email || !name || !cpfCnpj || !phone}
              sx={{
                py: 2,
                fontSize: '1.25rem',
                fontWeight: 800,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, #FF9800 0%, #F57C00 50%, #E65100 100%)',
                boxShadow: '0 8px 24px rgba(245, 124, 0, 0.45)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background:
                    'linear-gradient(135deg, #FFA726 0%, #FB8C00 50%, #EF6C00 100%)',
                  boxShadow: '0 10px 28px rgba(245, 124, 0, 0.6)',
                  transform: 'translateY(-2px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #aaa 100%)',
                  color: '#fff',
                  opacity: 0.7,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  🌱 Começar {plan.trialDays} dias grátis →
                </>
              )}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={1.5}
              sx={{ fontWeight: 500 }}
            >
              Cancele quando quiser. Sem multa. Primeira cobrança só no{' '}
              {plan.trialDays}º dia.
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={2}
            >
              Ao continuar, você concorda com nossos{' '}
              <a href="/termos" target="_blank" rel="noopener">
                Termos de Serviço
              </a>{' '}
              e{' '}
              <a href="/privacidade" target="_blank" rel="noopener">
                Política de Privacidade
              </a>
            </Typography>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Checkout;
